export interface UserSession {
  userId: string;
  playerName: string;
  displayName: string; // Alias for playerName for consistency
  createdAt: number;
  roomId?: string;
  hasGame?: boolean;
  isPlayer?: boolean;
  isSpectator?: boolean;
}

export interface User {
  userId: string;
  displayName: string;
}

const SESSION_KEY = "uno_user_session";

class SessionService {
  /**
   * Gets the current session from localStorage
   */
  getSession(): UserSession | null {
    try {
      const sessionData = localStorage.getItem(SESSION_KEY);
      if (!sessionData) {
        return null;
      }
      return JSON.parse(sessionData) as UserSession;
    } catch (error) {
      console.error("[SessionService] Error reading session:", error);
      return null;
    }
  }

  /**
   * Saves a session to localStorage
   */
  saveSession(session: UserSession): void {
    try {
      localStorage.setItem(SESSION_KEY, JSON.stringify(session));
      console.log("[SessionService] Session saved:", session.userId);
    } catch (error) {
      console.error("[SessionService] Error saving session:", error);
    }
  }

  /**
   * Updates session with new data (preserves userId if exists)
   */
  updateSession(updates: Partial<UserSession>): void {
    const currentSession = this.getSession();
    if (currentSession) {
      const updatedSession: UserSession = {
        ...currentSession,
        ...updates,
        userId: currentSession.userId, // Always preserve userId
        // Ensure displayName is set if playerName is updated
        displayName: updates.displayName || updates.playerName || currentSession.displayName || currentSession.playerName,
      };
      this.saveSession(updatedSession);
    } else if (updates.userId && (updates.playerName || updates.displayName)) {
      // Create new session if doesn't exist but we have required data
      const name = updates.displayName || updates.playerName || "Player";
      this.saveSession({
        userId: updates.userId,
        playerName: name,
        displayName: name,
        createdAt: Date.now(),
      });
    }
  }

  /**
   * Clears the session from localStorage
   */
  clearSession(): void {
    try {
      localStorage.removeItem(SESSION_KEY);
      console.log("[SessionService] Session cleared");
    } catch (error) {
      console.error("[SessionService] Error clearing session:", error);
    }
  }

  /**
   * Gets or creates a user
   * Generates userId on first visit, persists in localStorage
   * Returns userId and displayName
   */
  getOrCreateUser(displayName?: string): User {
    const session = this.getSession();
    
    // If session exists, return it
    if (session && session.userId) {
      return {
        userId: session.userId,
        displayName: displayName || session.displayName || session.playerName,
      };
    }
    
    // Generate new userId
    const userId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const name = displayName || "Player";
    
    // Save new session
    const newSession: UserSession = {
      userId,
      playerName: name,
      displayName: name,
      createdAt: Date.now(),
    };
    this.saveSession(newSession);
    
    return {
      userId,
      displayName: name,
    };
  }

  /**
   * Gets the user ID from session
   */
  getUserId(): string | null {
    const session = this.getSession();
    return session?.userId || null;
  }

  /**
   * Gets the player name from session
   */
  getPlayerName(): string | null {
    const session = this.getSession();
    return session?.displayName || session?.playerName || null;
  }
  
  /**
   * Updates display name
   * Updates both local storage and server via socket
   */
  updateDisplayName(displayName: string): void {
    // Update local storage first
    this.updateSession({
      playerName: displayName,
      displayName: displayName,
    });

    // Update server via socket if connected
    // Import dynamically to avoid circular dependency
    import("../socket/socket.client").then(({ socketClient }) => {
      if (socketClient.isSocketConnected()) {
        socketClient.updateUserName(displayName);
      }
    });
  }

  /**
   * Gets the room ID from session
   */
  getRoomId(): string | null {
    const session = this.getSession();
    return session?.roomId || null;
  }

  /**
   * Updates room information in session
   */
  updateRoomInfo(roomId: string, hasGame: boolean, isPlayer: boolean, isSpectator: boolean): void {
    this.updateSession({
      roomId,
      hasGame,
      isPlayer,
      isSpectator,
    });
  }

  /**
   * Clears room information from session
   */
  clearRoomInfo(): void {
    const session = this.getSession();
    if (session) {
      this.updateSession({
        roomId: undefined,
        hasGame: undefined,
        isPlayer: undefined,
        isSpectator: undefined,
      });
    }
  }
}

export const sessionService = new SessionService();
